import { BellIcon } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { useCan } from "@/features/auth/auth-context"
import {
  NotificationList,
  useDismissedNotifications,
} from "@/features/notifications/NotificationList"
import {
  useMarkAllNotificationsRead,
  useNotifications,
  useUnreadCount,
} from "@/features/notifications/use-notifications"
import { useIsMobile } from "@/hooks/use-mobile"
import { ApiError } from "@/lib/api-error"
import { cn } from "@/lib/utils"
import { routes } from "@/routes/paths"

/**
 * The bell, in the app chrome (spec §11.2, §17.7).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT SITS IN THE HEADER, AND IT DISTURBS NOTHING.
 *
 * §11.2's four action tiles are the largest element on the home screen and the
 * POINT of it — "Make a bill" is what the owner opened the app to do. The bell
 * is a compliance backstop, not a fifth thing to do, so it does not join them,
 * does not become a fifth tile, and does not take a sidebar row either (see
 * `routes.notifications` for why a nav item would be wrong).
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * IT OPENS A DRAWER RATHER THAN NAVIGATING, AND THE TRADE IS DELIBERATE.
 *
 * "What is waiting for me" is a GLANCE, and every glance used to cost a
 * round trip: leave the half-typed bill, read four rows, press Back, hope the
 * editor still holds what was typed. A panel over the current screen answers the
 * question without moving the reader off their work, which is the whole reason
 * the bell exists at the top of every screen in the first place.
 *
 * WHAT IS LOST is the anchor: a button cannot be middle-clicked or opened in a
 * new tab, which the previous `<Link>` deliberately could. That is paid back in
 * the drawer's footer, which carries a real `<Link>` to `/notifications` — so
 * the destination is one tap away, still bookmarkable, still sendable, and the
 * ROUTE ITSELF IS UNTOUCHED so every link already in somebody's history keeps
 * landing on the same list (see `NotificationsScreen`).
 *
 * RIGHT-HAND ON DESKTOP, BOTTOM SHEET ON A PHONE. A phone reaches its thumb at
 * the bottom of the screen, not the top-right corner it was tapped from, and a
 * sheet that can be swiped away is dismissed without aiming at anything —
 * `showSwipeHandle` says so visibly rather than leaving it to be discovered.
 *
 * THREE STATES, AND EACH RENDERS SOMETHING DIFFERENT:
 *
 *   NO CAPABILITY      nothing, and NO REQUEST EITHER. `notification.view` is
 *                      checked before the query runs, so a person who does not
 *                      hold it never polls a route that would 403 him once a
 *                      minute — and `requireCap` audits every denial (§2.1),
 *                      so that would be sixty audit rows an hour of noise.
 *   MODULE OFF (404)   nothing. §18.3 — a disabled feature is invisible, not
 *                      empty. A bell that opens an inbox reading "nothing yet"
 *                      promises alerts that will never come while the flag is
 *                      off.
 *   ON                 the bell, with a badge WHEN AND ONLY WHEN something is
 *                      unread.
 */
export function NotificationBell(): React.JSX.Element | null {
  const { t } = useTranslation()
  const canView = useCan("notification.view")
  const { unread, available } = useUnreadCount(canView)
  const [open, setOpen] = React.useState(false)
  const isMobile = useIsMobile()
  const dismissed = useDismissedNotifications()
  const markAll = useMarkAllNotificationsRead()

  /*
   * THE LIST IS ONLY FETCHED WHILE THE DRAWER IS OPEN. The bell polls a
   * `{"unread":3}` counter once a minute (see `useUnreadCount`); pulling fifty
   * rows on every one of those, on a metered workshop connection, to render a
   * panel nobody has opened would be a real cost for nothing. React Query
   * dedupes this against the identical key inside `NotificationList`, so the two
   * hooks are one request.
   */
  const list = useNotifications({ enabled: canView && open })
  /*
   * The rows a reader can actually see right now — the already-hidden ones are
   * excluded so "Clear all" disables itself on an empty panel rather than
   * offering to clear nothing. `isHidden` takes the ROW because "cleared" is
   * answered by `readAt`; see `useDismissedNotifications`.
   */
  const visible = (list.data?.notifications ?? []).filter((row) => !dismissed.isHidden(row))

  if (!canView || !available) return null

  /*
   * THE BELL STAYS AT ZERO; THE BADGE DOES NOT.
   *
   * Hiding the whole control when the inbox is empty would make the hub
   * unreachable exactly when somebody wants to go back and check what they were
   * told last week — and a chrome element that appears and vanishes is one the
   * eye stops trusting, so the day it IS there it reads as decoration. What
   * carries the information is the badge: a count is either a number or absent,
   * never a grey "0", because "0" is a claim that has to be read to be dismissed
   * and it is on screen all day.
   */
  const hasUnread = unread > 0

  return (
    <Drawer
      open={open}
      onOpenChange={setOpen}
      showSwipeHandle={isMobile}
      swipeDirection={isMobile ? "down" : "right"}
    >
      <DrawerTrigger
        className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "relative")}
        /*
         * THE COUNT IS IN THE ACCESSIBLE NAME, not only in the badge. The badge
         * is a visual dot with a number in it; a screen reader landing on a bare
         * "Notifications" button would have no way to learn that three things are
         * waiting. `count` also drives i18next's plural selection, so English
         * gets "1 unread" / "3 unread" and Marathi gets its own two forms.
         */
        aria-label={hasUnread ? t("notif.bellWithCount", { count: unread }) : t("notif.bell")}
        data-testid="notification-bell"
      >
        <BellIcon />
        {hasUnread ? (
          /*
           * `aria-hidden`: the number is already in the label above, and a screen
           * reader announcing "Notifications, 3 unread — 3" reads as a stutter.
           *
           * CAPPED AT 99+. The badge sits inside a 32px control; a four-digit
           * count would either overflow the header or shrink the type below the
           * point of having a badge. Nobody needs to know it is 214 rather than
           * "more than you are going to read one by one".
           */
          <span
            aria-hidden="true"
            data-testid="notification-badge"
            className="bg-destructive text-destructive-foreground absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-medium tabular-nums"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </DrawerTrigger>

      {/*
        * The width and the rounded edge come from `DrawerContent` itself, which
        * already answers both directions off `swipeDirection` — 24rem down the
        * right on a wide screen, full width along the bottom on a phone. Nothing
        * to override here.
        */}
      <DrawerContent data-testid="notifications-drawer">
        <DrawerHeader>
          <DrawerTitle>{t("notif.title")}</DrawerTitle>
          <DrawerDescription>
            {unread > 0 ? t("notif.unreadCount", { count: unread }) : t("notif.allCaughtUp")}
          </DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
          {/*
            * UNFRAMED: the drawer IS the surface, and a Card inside it would be a
            * border drawn a few pixels inside another border. Same component and
            * the same rows as `/notifications` either way.
            */}
          <NotificationList
            enabled={open}
            dismissed={dismissed}
            framed={false}
            onOpenRow={() => {
              // Opening a document takes the reader somewhere else; a panel left
              // hanging over the screen they asked for is a thing to dismiss
              // before they can read what they came for.
              setOpen(false)
            }}
          />
        </div>

        <DrawerFooter>
          {/*
            * "CLEAR ALL" IS `POST /read-all` PLUS A LOCAL HIDE. It is NOT a
            * delete and there is no endpoint that could be: the rows keep their
            * `read_at` so "was anybody ever told this bill needed an e-way bill"
            * has an answer months later, which is what a detention dispute turns
            * on. The hide lives in this component's state and survives no
            * refresh — the full list below still has every row.
            *
            * THE HIDE WAITS FOR THE POST, and a failure is said out loud. Both
            * used to be wrong in the same tap: `dismissAll` ran synchronously
            * beside `mutate()`, so a dropped request painted "These were marked
            * read, not deleted" over an inbox where NOTHING had been marked —
            * the bell still counting seven behind an empty panel, with no error
            * anywhere. `clearAll` also flags the surface as cleared, which is
            * what covers rows the server had not yet sent us; see
            * `useDismissedNotifications` for the paging trap that needs.
            */}
          <Button
            variant="outline"
            disabled={markAll.isPending || visible.length === 0}
            data-testid="notifications-clear-all"
            onClick={() => {
              markAll.mutate(undefined, {
                onSuccess: () => {
                  dismissed.clearAll(visible.map((row) => row.id))
                },
                onError: (error) => {
                  // The rows STAY, unread and countable. Saying so beats a panel
                  // that looks dealt with over a badge that will not go down.
                  toast.error(
                    error instanceof ApiError ? error.message : t("notif.clearAllFailed"),
                  )
                },
              })
            }}
          >
            {t("notif.clearAll")}
          </Button>

          {/*
            * A REAL `<Link>`, not a button that navigates — this is what pays
            * back the anchor the bell itself gave up. Middle-click and
            * open-in-new-tab work here, and the URL is the one that has been
            * bookmarkable since §17.7 shipped.
            */}
          <Link
            to={routes.notifications}
            className={cn(buttonVariants({ variant: "ghost" }))}
            onClick={() => {
              setOpen(false)
            }}
            data-testid="notifications-view-all"
          >
            {t("notif.viewAll")}
          </Link>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
